import { Component, Prop, h, Host } from '@stencil/core';
import type { Product } from '../../helpers/utils/interfaces';
import { renderTools } from '../../helpers/RenderTools';
import { renderArticleImg, renderEnergyLabels } from '../../helpers/helpers';

@Component({
  tag: 'cms-product-card',
  styleUrl: 'cms-product-card.scss',
  shadow: true,
})
export class CmsProductCard {
  /*
    Set the article of this product card
  */
  @Prop() article: string | Product;
  /*
    Set the size of the energy-label. (default: set according to priceSize)
  */
  @Prop() energyLabelSize?: string;
  /**
   * set accessible-name for the energy-label datasheet-link
   */
  @Prop() datasheetAccessibleName?: string;
  /*
    Set the accessible name of the rating element
  */
  @Prop() ratingTitle = '';
  /*
    Set the label of the cta
  */
  @Prop() ctaLabel?: string;
  /*
    Set the alt-text of the image
  */
  @Prop() imageAlt?: string;
  /*
    Set the max length of the article-name. (default: 0 --> unlimited)
  */
  @Prop() maxNameLength = 0;
  /*
    Set the size of the prize element (default: MD)
  */
  @Prop() priceSize = 'MD';
  /*
    Set the layout of the product card (vertical or horizontal) - default: vertical
  */
  @Prop() layout: 'vertical' | 'horizontal' = 'vertical';
  /*
    Set the style of the cta (button or link) - default: link
  */
  @Prop() ctaStyle: 'link' | 'button' = 'link';

  private product: Product;
  componentWillLoad() {
    // Product
    this.product =
      typeof this.article === 'string'
        ? JSON.parse(this.article)
        : this.article;
  }

  renderCta(label, url) {
    if (this.ctaStyle === 'button') {
      return (
        <cms-button
          style={{
            '--cms-theme-strong-hover':
              'var(--cms-semanticColors-transparency-strong-hover-background)',
            '--cms-button-border-color':
              'var(--cms-semanticColors-base-strong-border)',
          }}
          class='target-name'
          theme='secondary'
          size='button-md'
          href={url}
          rel='noopener'
          target='_self'
          label={label}
          tagTitle={label}
        />
      );
    }
    return (
      <cms-link
        class='target-name'
        href={url}
        type='standalone-inverted'
        linkTitle={label}
        content={label}
        size='lg'
        spacing='none'
        icon='arrowShaftRight'
        tag='a'
        style={{
          '--cms-text-color':
            'var(--cms-semanticColors-base-medium-iconOnBackground)',
        }}
      />
    );
  }

  render() {
    const {
      detailsUrl,
      name,
      image,
      price,
      priceUnit,
      priceCurrency,
      pricePrefix,
      bracketPrice,
      bracketPriceUnit,
      ratingValue,
      ratingCount,
      imageAltText,
    } = this.product;

    const ctaLabel = this.ctaLabel;
    const imageAlt = imageAltText || this.imageAlt || name;

    return (
      <Host>
        <div class='article-info-container'>
          <div class={`article-info-wrapper ${this.layout}`}>
            <div class={'article-image-wrapper'}>
              {renderArticleImg(imageAlt, image)}
            </div>
            <div class='article-data-container'>
              <div class={'article-data-wrapper'}>
                <a class='cta' aria-label={ctaLabel ?? name} href={detailsUrl}>
                  {name.length <= 40 || this.maxNameLength === 0 ? (
                    <cms-article-name name={name} />
                  ) : (
                    <cms-article-name
                      name={renderTools.shortenLabel(name, this.maxNameLength)}
                    />
                  )}
                </a>
                {ratingValue > 0 && ratingCount > 0 ? (
                  <cms-rating
                    style={{
                      color: 'var(--cms-semanticColors-base-strong-text)',
                    }}
                    ratingValue={ratingValue}
                    ratingCount={ratingCount}
                    titleText={this.ratingTitle}
                  />
                ) : (
                  ''
                )}
              </div>

              <div class='price-wrapper'>
                <cms-price
                  price={price}
                  unit={priceUnit}
                  currency={priceCurrency}
                  pricePrefix={pricePrefix}
                  bracketPrice={bracketPrice}
                  bracketPriceUnit={bracketPriceUnit}
                  size={this.priceSize}
                />
              </div>
              {renderEnergyLabels(
                this.product,
                this.datasheetAccessibleName,
                this.energyLabelSize || this.priceSize,
              )}
              {ctaLabel ? this.renderCta(ctaLabel, detailsUrl) : ''}
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
